import * as React from "react";
import {
  Link,
  Stack,
  Box,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Select,
  MenuItem,
  Paper,
  InputBase,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUp from "@mui/icons-material/ArrowDropUp";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";

import { getHeaders } from "../utils/HeaderHelper";
import { AuthenticationContext, ImagesContext } from "../Context";
import LoadingPatientRoster from "./LoadingPatientRoster";
import EmptyPatientRoster from "./EmptyPatientRoster";
import BadgeList from "../common/BadgeList";
import PatientRosterAdvancedFilters, {
  DEFAULT_PATIENT_ROSTER_FILTERS,
  PatientRosterFilters,
  PlanEligibilityValue,
  ProgramEligibilityValue,
  EnrollmentValue,
} from "./PatientRosterAdvancedFilters";

type FilterBadgeType =
  | "payer"
  | "plan_eligibility"
  | "program_eligibility"
  | "program"
  | "enrollment"
  | "do_not_contact";

interface PatientRosterBadgeChip {
  key: string;
  label: string;
  type: FilterBadgeType;
  value: string | number;
}

interface FilterOption {
  id: number;
  name: string;
}

interface RosterProgram {
  id: number | null;
  program_name: string | null;
  eligibility: string | null;
  enrollment_status: string | null;
}

interface DoNotContactStatus {
  enabled: boolean;
  status_label: string | null;
  effective_date: string | null;
  relative_label: string | null;
}

type PlanEligibilityValueStr = "Plan Eligible" | "Plan Ineligible" | string;
interface LastOutreach {
  date: string | null;
  author: string | null;
}
interface NextReminderDueDate {
  date: string | null;
  relative_label: string | null;
  is_overdue: boolean;
}

interface RosterPatient {
  id: number;
  patient_name: string;
  patient_profile_path: string;
  plan_eligibility: PlanEligibilityValueStr | null;
  associated_payer: string | null;
  do_not_contact_status: DoNotContactStatus | null;
  last_outreach: LastOutreach | null;
  last_outreach_status: string | null;
  next_reminder_due_date: NextReminderDueDate | null;
  programs: RosterProgram[];
}

type SortField = "patient_name" | "associated_payer";
type SortDirection = "ascending" | "descending";

interface SortObject {
  field: SortField;
  direction: SortDirection;
}
const DASH = "--";


const COLUMN_WIDTHS: Record<string, string> = {
  patient_name: "14%",
  associated_payer: "12%",
  do_not_contact: "10%",
  payer_programs: "16%",
  eligibility: "9%",
  enrollment_status: "9%",
  last_outreach: "9%",
  last_outreach_status: "11%",
  next_reminder_due_date: "10%",
};

const SECTION_DIVIDER = "1px solid #D9D5D3";

const displayOrDash = (value: string | null | undefined): string => {
  if (value === null || value === undefined) return DASH;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : DASH;
};


interface SortableHeaderCellProps {
  columnName: SortField;
  columnTitle: string;
  sortObject: SortObject;
  setSortOrder: (field: SortField, direction: SortDirection) => void;
  width: string;
}

const SortableHeaderCell: React.FC<SortableHeaderCellProps> = ({
  columnName,
  columnTitle,
  sortObject,
  setSortOrder,
  width,
}) => {
  const images = React.useContext(ImagesContext);
  const isActive = sortObject.field === columnName;

  return (
    <TableCell
      align="left"
      width={width}
      sx={{ width, minWidth: 0, cursor: "pointer", whiteSpace: "normal" }}
      onClick={() => {
        setSortOrder(
          columnName,
          isActive && sortObject.direction === "ascending"
            ? "descending"
            : "ascending",
        );
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <span>{columnTitle}</span>
        <span className="sortIndicator" style={{ display: "inline-flex" }}>
          {isActive ? (
            sortObject.direction === "ascending" ? (
              <img
                src={images.sort_ascending_src}
                width="10"
                className="sort-icon"
                alt="Sort Asc"
              />
            ) : (
              <img
                src={images.sort_descending_src}
                width="10"
                className="sort-icon"
                alt="Sort Desc"
              />
            )
          ) : (
            <img
              src={images.sort_plain_src}
              width="10"
              className="sort-icon"
              alt="Sort"
            />
          )}
        </span>
      </Box>
    </TableCell>
  );
};

const PLAN_ELIGIBILITY_VARIANTS: Record<string, { bg: string; color: string }> = {
  "Plan Eligible":   { bg: "#EDF7EE", color: "#07BC2A" },
  "Plan Ineligible": { bg: "#FBEAEA", color: "#FF0000" },
};

interface DateWithSubLabelCellProps {
  primaryText: string | null | undefined;
  subText?: string | null;
  isAlert?: boolean;
}

const DateWithSubLabelCell: React.FC<DateWithSubLabelCellProps> = ({
  primaryText,
  subText,
  isAlert = false,
}) => {
  if (!primaryText) {
    return <span>{DASH}</span>;
  }

  const primaryColor = isAlert ? "#FF0000" : "#1E1E1E";
  const subColor = isAlert ? "#FF0000" : "#868382";
  const fontWeight = isAlert ? 600 : 400;

  return (
    <Box>
      <Typography
        sx={{
          fontSize: "12px",
          color: primaryColor,
          fontWeight,
          lineHeight: "16px",
        }}
      >
        {primaryText}
      </Typography>
      {subText ? (
        <Typography
          sx={{
            fontSize: "11px",
            color: subColor,
            fontWeight,
            lineHeight: "14px",
          }}
        >
          {subText}
        </Typography>
      ) : null}
    </Box>
  );
};

interface StatusBadgeProps {
  value: string | null | undefined;
  variants: Record<string, { bg: string; color: string }>;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ value, variants }) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return <span>{DASH}</span>;
  }

  const normalized = String(value).trim();
  const variant = variants[normalized] || { bg: "#EFE9E7", color: "#4A4442" };

  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: "12px",
        backgroundColor: variant.bg,
        color: variant.color,
        fontSize: "11px",
        fontWeight: 600,
        lineHeight: "16px",
        whiteSpace: "nowrap",
      }}
    >
      {normalized}
    </Box>
  );
};

const PROGRAM_ELIGIBILITY_VARIANTS: Record<string, { bg: string; color: string }> = {
  Eligible: { bg: "#EDF7EE", color: "#07BC2A" },
  "Not Eligible": { bg: "#FBEAEA", color: "#FF0000" },
  Pending: { bg: "#FFF7E7", color: "#E59E3F" },
};

const ENROLLMENT_VARIANTS: Record<string, { bg: string; color: string }> = {
  Enrolled: { bg: "#EDF7EE", color: "#07BC2A" },
  "Not Enrolled": { bg: "#FBEAEA", color: "#FF0000" },
  Unenrolled: { bg: "#EFE9E7", color: "#4A4442" },
};

interface AssociatedPayerCellProps {
  value: string | null | undefined;
}

const AssociatedPayerCell: React.FC<AssociatedPayerCellProps> = ({ value }) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return <span>{DASH}</span>;
  }
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: "12px",
        backgroundColor: "#EDF7EE",
        color: "#07BC2A",
        fontSize: "11px",
        fontWeight: 600,
        lineHeight: "16px",
      }}
    >
      {String(value).trim()}
    </Box>
  );
};

const LAST_OUTREACH_STATUS_VARIANTS: Record<
  string,
  { bg: string; color: string }
> = {
  Successful: { bg: "#EDF7EE", color: "#07BC2A" },
  Unsuccessful: { bg: "#FBEAEA", color: "#FF0000" },
};

const PatientRoster: React.FC = () => {
  const authenticationSetting = React.useContext(AuthenticationContext);

  const [patients, setPatients] = React.useState<RosterPatient[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [fetchError, setFetchError] = React.useState<string | null>(null);

  const [lastNameSearchInput, setLastNameSearchInput] = React.useState<string>("");
  const [lastName, setLastName] = React.useState<string>("");

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const advancedFilterTriggerRef = React.useRef<HTMLDivElement | null>(null);
  const [payerOptions, setPayerOptions] = React.useState<FilterOption[]>([]);
  const [programOptions, setProgramOptions] = React.useState<FilterOption[]>([]);
  const [filters, setFilters] = React.useState<PatientRosterFilters>(
    DEFAULT_PATIENT_ROSTER_FILTERS,
  );

  const [badgeData, setBadgeData] = React.useState<PatientRosterBadgeChip[]>([]);

  const [sortObject, setSortObject] = React.useState<SortObject>({
    field: "patient_name",
    direction: "ascending",
  });

  const setSortOrder = React.useCallback((field: SortField, direction: SortDirection) => {
    setSortObject({ field, direction });
  }, []);

  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [perPage, setPerPage] = React.useState<number>(25);
  const [totalCount, setTotalCount] = React.useState<number>(0);
  const [totalPages, setTotalPages] = React.useState<number>(0);

  const toggleAdvancedFilter = () => {
    if (loading) return;
    const opening = anchorEl === null;
    setAnchorEl((prev) =>
      prev === null ? advancedFilterTriggerRef.current : null,
    );
    if (opening && payerOptions.length === 0 && programOptions.length === 0) {
      fetch("/patient_rosters/filter_options", {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      })
        .then((res) => res.json())
        .then((result) => {
          if (result?.success && result?.resource) {
            if (Array.isArray(result.resource.payers)) setPayerOptions(result.resource.payers);
            if (Array.isArray(result.resource.programs)) setProgramOptions(result.resource.programs);
          }
        })
        .catch(() => {
          setFetchError("Failed to load filter options.");
        });
    }
  };

  const getPatientRoster = React.useCallback(async () => {
    setLoading(true);
    setFetchError(null);

    try {
      const query = new URLSearchParams();
      query.set("page", String(currentPage));
      query.set("per_page", String(perPage));
      query.set("sort_by", sortObject.field);
      query.set("sort_dir", sortObject.direction === "ascending" ? "asc" : "desc");

      if (lastName.trim().length >= 2) {
        query.set("last_name", lastName.trim());
      }

      if (filters.payerId !== "all") {
        query.append("payer_ids[]", String(filters.payerId));
      }

      filters.planEligibility.forEach((v) =>
        query.append("plan_eligibility[]", v),
      );

      filters.programEligibility.forEach((v) =>
        query.append("program_eligibility[]", v),
      );

      filters.programIds.forEach((id) =>
        query.append("program_ids[]", String(id)),
      );

      filters.enrollment.forEach((v) => query.append("enrollment[]", v));

      if (filters.excludeDoNotContact) {
        query.set("exclude_do_not_contact", "true");
      }

      const response = await fetch(`/patient_rosters?${query.toString()}`, {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      });

      const result = await response.json();

      if (result?.success && result?.resource) {
        const rawPatients: RosterPatient[] = Array.isArray(
          result.resource.patients,
        )
          ? result.resource.patients
          : [];

        setPatients(rawPatients);

        if (result.resource.pagination) {
          setTotalCount(result.resource.pagination.total_count);
          setTotalPages(result.resource.pagination.total_pages);
        } else {
          setTotalCount(rawPatients.length);
          setTotalPages(1);
        }
      } else {
        setFetchError(result?.message || "Failed to fetch patient roster.");
        setPatients([]);
        setTotalCount(0);
        setTotalPages(0);
      }
    } catch (error) {
      setFetchError("An error occurred while loading the patient roster. Please try again.");
      setPatients([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [
    authenticationSetting.csrfToken,
    currentPage,
    perPage,
    lastName,
    filters,
    sortObject,
  ]);

  const getFilterOptions = React.useCallback(async () => {
    try {
      const response = await fetch("/patient_rosters/filter_options", {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      });
      const result = await response.json();
      if (result?.success && result?.resource) {
        if (Array.isArray(result.resource.payers)) {
          setPayerOptions(result.resource.payers);
        }
        if (Array.isArray(result.resource.programs)) {
          setProgramOptions(result.resource.programs);
        }
      }
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  }, [authenticationSetting.csrfToken]);

  React.useEffect(() => {
    getPatientRoster();
  }, [getPatientRoster]);

  React.useEffect(() => {
    getFilterOptions();
  }, [getFilterOptions]);

  React.useEffect(() => {
    const trimmed = lastNameSearchInput.trim();
    const committed = trimmed.length >= 2 ? trimmed : "";

    const timeout = window.setTimeout(() => {
      setLastName((prev) => (prev === committed ? prev : committed));
    }, 250);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [lastNameSearchInput]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [lastName, filters]);

  React.useEffect(() => {
    const next: PatientRosterBadgeChip[] = [];

    if (filters.payerId !== "all") {
      const payerName =
        payerOptions.find((opt) => opt.id === filters.payerId)?.name ||
        `Payer ${filters.payerId}`;
      next.push({
        key: `payer-${payerName}`,
        label: payerName,
        type: "payer",
        value: filters.payerId,
      });
    }

    filters.planEligibility.forEach((v) => {
      const label = v === "plan_eligible" ? "Plan Eligible" : "Plan Ineligible";
      next.push({
        key: `plan_eligibility-${v}`,
        label,
        type: "plan_eligibility",
        value: v,
      });
    });

    filters.programEligibility.forEach((v) => {
      const label =
        v === "eligible" ? "Program Eligible" : "Program Not Eligible";
      next.push({
        key: `program_eligibility-${v}`,
        label,
        type: "program_eligibility",
        value: v,
      });
    });

    filters.programIds.forEach((id) => {
      const programName =
        programOptions.find((opt) => opt.id === id)?.name || `Program ${id}`;
      next.push({
        key: `program-${programName}`,
        label: programName,
        type: "program",
        value: id,
      });
    });

    filters.enrollment.forEach((v) => {
      const label = v === "enrolled" ? "Enrolled" : "Not Enrolled";
      next.push({
        key: `enrollment-${v}`,
        label,
        type: "enrollment",
        value: v,
      });
    });

    if (filters.excludeDoNotContact) {
      next.push({
        key: "do_not_contact-exclude",
        label: "Exclude Do Not Contact",
        type: "do_not_contact",
        value: "exclude",
      });
    }

    setBadgeData(next);
  }, [filters, payerOptions, programOptions]);


  const handleDeleteBadge = (badge: PatientRosterBadgeChip) => {
    switch (badge.type) {
      case "payer":
        setFilters((prev) => ({ ...prev, payerId: "all" }));
        break;
      case "plan_eligibility":
        setFilters((prev) => ({
          ...prev,
          planEligibility: prev.planEligibility.filter(
            (v) => v !== (badge.value as PlanEligibilityValue),
          ),
        }));
        break;
      case "program_eligibility":
        setFilters((prev) => ({
          ...prev,
          programEligibility: prev.programEligibility.filter(
            (v) => v !== (badge.value as ProgramEligibilityValue),
          ),
        }));
        break;
      case "program":
        setFilters((prev) => ({
          ...prev,
          programIds: prev.programIds.filter((id) => id !== badge.value),
        }));
        break;
      case "enrollment":
        setFilters((prev) => ({
          ...prev,
          enrollment: prev.enrollment.filter(
            (v) => v !== (badge.value as EnrollmentValue),
          ),
        }));
        break;
      case "do_not_contact":
        setFilters((prev) => ({ ...prev, excludeDoNotContact: false }));
        break;
      default:
        break;
    }
  };

  const selectPage = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <Box
      className="report-main-content"
      sx={{ paddingTop: "10px", paddingLeft: "120px" }}
    >
      <Box
        className="main-header"
        sx={{ paddingY: "24px", marginBottom: "26px" }}
      >
        <Box className="heading-with-back-button-section">
          <Stack direction="column" rowGap="2px">
            <Typography
              sx={{
                fontSize: "24px",
                color: "black",
                fontWeight: 700,
                lineHeight: "normal",
              }}
            >
              Patient Roster
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#4A4442",
                fontWeight: 400,
                lineHeight: "20px",
              }}
            >
              View and manage your population by eligibility, enrollment, and
              outreach status.
            </Typography>
          </Stack>
        </Box>
      </Box>

      <Box className="report-details">
        <Box
          className="sub-header"
          sx={{
            paddingX: "24px",
            paddingY: "17.25px",
            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.6)",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box className="subheading-with-pill-section">
            <Typography className="sub-heading">Patient Count</Typography>
            <Box className="count-pill">{totalCount}</Box>
          </Box>

          <Box className="filters-section">
            <Paper className="input-paper">
              <InputBase
                placeholder="Search Last Name"
                inputProps={{ "aria-label": "search last name" }}
                value={lastNameSearchInput}
                onChange={(e) => setLastNameSearchInput(e.target.value)}
              />
              <SearchIcon sx={{ color: "#707070" }} />
            </Paper>

            <div
              ref={advancedFilterTriggerRef}
              role="button"
              tabIndex={loading ? -1 : 0}
              className="advanced-search-container"
              onClick={loading ? undefined : toggleAdvancedFilter}
              onKeyDown={
                loading
                  ? undefined
                  : (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleAdvancedFilter();
                      }
                    }
              }
              style={{
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.8 : 1,
              }}
            >
              <FilterListIcon sx={{ width: 20, height: 20 }} />
              <p className="advanced-search-text">Advanced Filters</p>
              {anchorEl ? (
                <ArrowDropUp sx={{ width: 24, height: 24 }} />
              ) : (
                <ArrowDropDownIcon sx={{ width: 24, height: 24 }} />
              )}
            </div>

            <PatientRosterAdvancedFilters
              anchorEl={anchorEl}
              setAnchorEl={setAnchorEl}
              payerOptions={payerOptions}
              programOptions={programOptions}
              filters={filters}
              setFilters={setFilters}
              disabled={loading}
            />
          </Box>
        </Box>

        <Box className="table-section" sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          {badgeData?.length > 0 && (
            <div
              className="badge-container"
              style={{ width: "auto", marginBottom: "20px" }}
            >
              <BadgeList
                badgeData={badgeData}
                setBadgeData={setBadgeData}
                handleDeleteBadge={handleDeleteBadge}
                background="none"
              />
            </div>
          )}

          {fetchError && (
            <Typography color="error" sx={{ px: 3, py: 2 }}>
              {fetchError}
            </Typography>
          )}

          {loading ? (
            <LoadingPatientRoster isBadgeShowing={badgeData.length > 0} />
          ) : (
            <>
              {patients?.length > 0 ? (
                <TableContainer
                  className="report-table-container"
                  sx={{ flex: 1, overflowX: "hidden", overflowY: "auto" }}
                >
                  <Table
                    stickyHeader
                    sx={{
                      tableLayout: "fixed",
                      width: "100%",
                    }}
                  >
                    <TableHead className="table-header">
                      <TableRow
                        sx={{
                          "& td, & th": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        <SortableHeaderCell
                          columnName="patient_name"
                          columnTitle="Patient Name"
                          sortObject={sortObject}
                          setSortOrder={setSortOrder}
                          width={COLUMN_WIDTHS.patient_name}
                        />

                        <SortableHeaderCell
                          columnName="associated_payer"
                          columnTitle="Associated Payer"
                          sortObject={sortObject}
                          setSortOrder={setSortOrder}
                          width={COLUMN_WIDTHS.associated_payer}
                        />

                        <TableCell
                          align="left"
                          width={COLUMN_WIDTHS.do_not_contact}
                          sx={{ width: COLUMN_WIDTHS.do_not_contact, minWidth: 0 }}
                        >
                          Do Not Contact Status
                        </TableCell>

                        <TableCell
                          align="left"
                          width={COLUMN_WIDTHS.payer_programs}
                          sx={{
                            width: COLUMN_WIDTHS.payer_programs,
                            minWidth: 0,
                          }}
                        >
                          Payer Program(s)
                        </TableCell>

                        <TableCell
                          align="left"
                          width={COLUMN_WIDTHS.eligibility}
                          sx={{ width: COLUMN_WIDTHS.eligibility, minWidth: 0 }}
                        >
                          Eligibility
                        </TableCell>

                        <TableCell
                          align="left"
                          width={COLUMN_WIDTHS.enrollment_status}
                          sx={{
                            width: COLUMN_WIDTHS.enrollment_status,
                            minWidth: 0,
                          }}
                        >
                          Enrollment Status
                        </TableCell>

                        <TableCell
                          align="left"
                          width={COLUMN_WIDTHS.last_outreach}
                          sx={{ width: COLUMN_WIDTHS.last_outreach, minWidth: 0 }}
                        >
                          Last Outreach
                        </TableCell>

                        <TableCell
                          align="left"
                          width={COLUMN_WIDTHS.last_outreach_status}
                          sx={{
                            width: COLUMN_WIDTHS.last_outreach_status,
                            minWidth: 0,
                          }}
                        >
                          Last Outreach Status
                        </TableCell>

                        <TableCell
                          align="left"
                          width={COLUMN_WIDTHS.next_reminder_due_date}
                          sx={{
                            width: COLUMN_WIDTHS.next_reminder_due_date,
                            minWidth: 0,
                          }}
                        >
                          Next Reminder Due Date
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {patients.map((patient) => {
                        const programs =
                          patient.programs && patient.programs.length > 0
                            ? patient.programs
                            : [
                                {
                                  id: null,
                                  program_name: null,
                                  eligibility: null,
                                  enrollment_status: null,
                                },
                              ];

                        return programs.map((program, programIndex) => {
                          const isFirstProgramRow = programIndex === 0;
                          const isLastProgramRow =
                            programIndex === programs.length - 1;

                          const cellBorder = isLastProgramRow
                            ? "1px solid #EFE9E7"
                            : "none";

                          const baseCellSx = {
                            verticalAlign: "top",
                            borderBottom: cellBorder,
                            wordBreak: "break-word" as const,
                            whiteSpace: "normal" as const,
                          };
                          const centeredCellSx = {
                            ...baseCellSx,
                            verticalAlign: "middle" as const,
                          };

                          return (
                            <TableRow
                              key={`${patient.id}-${
                                program.id ?? `placeholder-${programIndex}`
                              }`}
                            >
                              
                              <TableCell
                                align="left"
                                sx={{
                                  ...baseCellSx,
                                  width: COLUMN_WIDTHS.patient_name,
                                }}
                              >
                                {isFirstProgramRow ? (
                                  <Box>
                                    <Link
                                      href={patient.patient_profile_path}
                                      className="table-body-link"
                                      sx={{
                                        color: "#1976D2",
                                        fontWeight: 500,
                                        textDecoration: "none",
                                        cursor: "pointer",
                                      }}
                                    >
                                      {patient.patient_name}
                                    </Link>
                                    {patient.plan_eligibility ? (
                                      <Box sx={{ marginTop: "4px" }}>
                                        <StatusBadge
                                          value={patient.plan_eligibility}
                                          variants={PLAN_ELIGIBILITY_VARIANTS}
                                        />
                                      </Box>
                                    ) : null}
                                  </Box>
                                ) : null}
                              </TableCell>

                              <TableCell
                                align="left"
                                sx={{
                                  ...centeredCellSx,
                                  width: COLUMN_WIDTHS.associated_payer,
                                }}
                              >
                                {isFirstProgramRow ? (
                                  <AssociatedPayerCell
                                    value={patient.associated_payer}
                                  />
                                ) : null}
                              </TableCell>

                              <TableCell
                                align="left"
                                sx={{
                                  ...centeredCellSx,
                                  width: COLUMN_WIDTHS.do_not_contact,
                                }}
                              >
                                {isFirstProgramRow ? (
                                  <DateWithSubLabelCell
                                    primaryText={patient.do_not_contact_status?.enabled ? patient.do_not_contact_status.effective_date : null}
                                    subText={patient.do_not_contact_status?.enabled ? patient.do_not_contact_status.relative_label : null}
                                  />
                                ) : null}
                              </TableCell>


                              <TableCell
                                align="left"
                                sx={{
                                  ...baseCellSx,
                                  width: COLUMN_WIDTHS.payer_programs,
                                  borderLeft: SECTION_DIVIDER,
                                }}
                              >
                                {displayOrDash(program.program_name)}
                              </TableCell>

                              <TableCell
                                align="left"
                                sx={{
                                  ...centeredCellSx,
                                  width: COLUMN_WIDTHS.eligibility,
                                }}
                              >
                                <StatusBadge
                                  value={program.eligibility}
                                  variants={PROGRAM_ELIGIBILITY_VARIANTS}
                                />
                              </TableCell>


                              <TableCell
                                align="left"
                                sx={{
                                  ...centeredCellSx,
                                  width: COLUMN_WIDTHS.enrollment_status,
                                  borderRight: SECTION_DIVIDER,
                                }}
                              >
                                <StatusBadge
                                  value={program.enrollment_status}
                                  variants={ENROLLMENT_VARIANTS}
                                />
                              </TableCell>

                              <TableCell
                                align="left"
                                sx={{
                                  ...centeredCellSx,
                                  width: COLUMN_WIDTHS.last_outreach,
                                }}
                              >
                                {isFirstProgramRow ? (
                                  <DateWithSubLabelCell
                                    primaryText={patient.last_outreach?.date ?? null}
                                    subText={patient.last_outreach?.author ? `Created by ${patient.last_outreach.author}` : null}
                                  />
                                ) : null}
                              </TableCell>

                              <TableCell
                                align="left"
                                sx={{
                                  ...centeredCellSx,
                                  width: COLUMN_WIDTHS.last_outreach_status,
                                }}
                              >
                                {isFirstProgramRow ? (
                                  <StatusBadge
                                    value={patient.last_outreach_status}
                                    variants={LAST_OUTREACH_STATUS_VARIANTS}
                                  />
                                ) : null}
                              </TableCell>

                              <TableCell
                                align="left"
                                sx={{
                                  ...centeredCellSx,
                                  width: COLUMN_WIDTHS.next_reminder_due_date,
                                }}
                              >
                                {isFirstProgramRow ? (
                                  <DateWithSubLabelCell
                                    primaryText={patient.next_reminder_due_date?.date ?? null}
                                    subText={patient.next_reminder_due_date?.relative_label ?? null}
                                    isAlert={!!patient.next_reminder_due_date?.is_overdue}
                                  />
                                ) : null}
                              </TableCell>
                            </TableRow>
                          );
                        });
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <EmptyPatientRoster isBadgeShowing={badgeData.length > 0} />
              )}
            </>
          )}

          {patients.length > 0 && !loading && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                backgroundColor: "white",
              }}
            >
              <Typography sx={{ fontSize: "14px" }}>Rows per page</Typography>
              <Select
                id="patient-roster-page-size-select"
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                disabled={loading}
                size="small"
                sx={{
                  minWidth: 70,
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
              {totalPages > 0 && (
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(
                    _event: React.ChangeEvent<unknown>,
                    page: number,
                  ) => {
                    selectPage(page);
                  }}
                  boundaryCount={1}
                  siblingCount={1}
                  showFirstButton
                  showLastButton
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PatientRoster;
